import { useEffect, useMemo, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet-distortableimage/dist/vendor.js';
import 'leaflet-distortableimage';
import 'leaflet-distortableimage/dist/vendor.css';
import 'leaflet-distortableimage/dist/leaflet.distortableimage.css';

import type { OverlayPlacement } from '../types';

type DistortableImageLayerProps = {
  imageUrl: string | null;
  placement: OverlayPlacement | null;
  editable?: boolean;
  mode?: 'drag' | 'scale' | 'rotate' | 'freeRotate';
  preserveAspectRatio?: boolean;
  onTogglePreserveAspectRatio?: () => void;
  onOverlayClick?: (lat: number, lng: number) => void;
  opacity?: number;
  onReady?: (overlay: L.DistortableImageOverlay | null) => void;
};

const TOOLBAR_TOGGLE_OPACITY = 0.4;

const ensureAspectRatioToggleAction = () => {
  const leaflet = L as any;

  if (leaflet.AspectRatioToggleAction || !leaflet.EditAction) {
    return;
  }

  leaflet.AspectRatioToggleAction = leaflet.EditAction.extend({
    initialize(map: unknown, overlay: L.DistortableImageOverlay, options: any) {
      options = options || {};
      options.toolbarIcon = {
        svg: false,
        html: '',
        tooltip: 'Preklopi ohranjanje razmerja',
        className: 'aspect-ratio-toggle',
      };

      leaflet.EditAction.prototype.initialize.call(this, map, overlay, options);
    },
    addHooks() {
      const onToggle = (this as any)?._overlay?.options?.onAspectRatioToggle;
      if (typeof onToggle === 'function') {
        onToggle();
      }
    },
  });
};

const ensureDistortableCleanupGuards = () => {
  const leaflet = L as any;

  if (leaflet.__graveSearchDistortableCleanupPatched) {
    return;
  }

  const editHandlePrototype = leaflet.EditHandle?.prototype;
  if (!editHandlePrototype) {
    return;
  }

  editHandlePrototype._unbindListeners = function unbindListenersSafely() {
    this.off(
      {
        contextmenu: L.DomEvent.stop,
        dragstart: this._onHandleDragStart,
        drag: this._onHandleDrag,
        dragend: this._onHandleDragEnd,
      },
      this,
    );

    const handledMap = this._handled?._map;
    if (handledMap) {
      handledMap.off('zoomend', this.updateHandle, this);
    }

    this._handled?.off?.('update', this.updateHandle, this);
  };

  const lockHandlePrototype = leaflet.LockHandle?.prototype;
  if (lockHandlePrototype) {
    lockHandlePrototype._unbindListeners = function unbindLockListenersSafely() {
      const icon = this.getElement?.();

      leaflet.EditHandle.prototype._unbindListeners.call(this);

      if (icon) {
        L.DomEvent.off(
          icon,
          {
            mousedown: this._tooltipOn,
            mouseup: this._tooltipOff,
          },
          this,
        );
      }

      L.DomEvent.off(document as unknown as HTMLElement, 'pointerleave', this._tooltipOff, this);
    };
  }

  leaflet.__graveSearchDistortableCleanupPatched = true;
};

const ensureDistortableOpacityToggleDefault = (opacity: number) => {
  const leaflet = L as any;
  const editPrototypeOptions = leaflet.DistortableImage?.Edit?.prototype?.options;

  if (!editPrototypeOptions) {
    return;
  }

  editPrototypeOptions.opacity = opacity;
};

const DistortableImageLayer: React.FC<DistortableImageLayerProps> = ({
  imageUrl,
  placement,
  editable = false,
  mode = 'freeRotate',
  preserveAspectRatio = true,
  onTogglePreserveAspectRatio,
  onOverlayClick,
  opacity = 1,
  onReady,
}) => {
  const map = useMap();
  const overlayRef = useRef<L.DistortableImageOverlay | null>(null);
  const overlayContainerRef = useRef<HTMLElement | null>(null);
  const toggleAspectRatioRef = useRef<typeof onTogglePreserveAspectRatio>(onTogglePreserveAspectRatio);
  const overlayClickRef = useRef<typeof onOverlayClick>(onOverlayClick);
  const placementKey = useMemo(() => JSON.stringify(placement ?? null), [placement]);

  useEffect(() => {
    toggleAspectRatioRef.current = onTogglePreserveAspectRatio;
  }, [onTogglePreserveAspectRatio]);

  useEffect(() => {
    overlayClickRef.current = onOverlayClick;
  }, [onOverlayClick]);

  const syncAspectRatioToggleButton = () => {
    if (!editable || !overlayContainerRef.current) {
      return;
    }

    const toolbar = overlayContainerRef.current.querySelector('.leaflet-popup-toolbar') as HTMLElement | null;
    if (!toolbar) {
      return;
    }

    let toggleAnchor = toolbar.querySelector('a[data-aspect-ratio-toggle="true"]') as HTMLAnchorElement | null;

    if (!toggleAnchor) {
      toggleAnchor = toolbar.querySelector('a.leaflet-toolbar-icon.aspect-ratio-toggle') as HTMLAnchorElement | null;
      if (toggleAnchor) {
        toggleAnchor.setAttribute('data-aspect-ratio-toggle', 'true');
      }
    }

    if (!toggleAnchor) {
      return;
    }

    toggleAnchor.classList.toggle('aspect-ratio-on', preserveAspectRatio);
    toggleAnchor.classList.toggle('aspect-ratio-off', !preserveAspectRatio);
    toggleAnchor.setAttribute(
      'title',
      preserveAspectRatio
        ? 'Razmerje vklopljeno (proporcionalna sprememba velikosti)'
        : 'Razmerje izklopljeno (prosta deformacija)',
    );
  };

  useEffect(() => {
    if (!imageUrl) {
      onReady?.(null);
      return;
    }
    if (typeof L.distortableImageOverlay !== 'function') {
      console.error('leaflet-distortableimage plugin is not available.');
      onReady?.(null);
      return;
    }

    let isDisposed = false;
    ensureAspectRatioToggleAction();
    ensureDistortableCleanupGuards();
    ensureDistortableOpacityToggleDefault(TOOLBAR_TOGGLE_OPACITY);

    const leaflet = L as any;

    const corners = placement
      ? [
          L.latLng(placement.topLeft.lat, placement.topLeft.lng),
          L.latLng(placement.topRight.lat, placement.topRight.lng),
          L.latLng(placement.bottomLeft.lat, placement.bottomLeft.lng),
          L.latLng(placement.bottomRight.lat, placement.bottomRight.lng),
        ]
      : undefined;

    const overlayOptions: L.DistortableImageOverlayOptions & { onAspectRatioToggle?: () => void } = {
      editable,
      selected: false,
      suppressToolbar: !editable,
      mode,
      opacity,
      corners,
      onAspectRatioToggle: () => toggleAspectRatioRef.current?.(),
      actions: editable
        ? [
            L.DragAction,
            preserveAspectRatio ? L.ScaleAction : L.DistortAction,
            L.RotateAction,
            L.FreeRotateAction,
            L.OpacityAction,
            L.LockAction,
            leaflet.AspectRatioToggleAction,
          ]
        : undefined,
    };

    const overlay = L.distortableImageOverlay(imageUrl, overlayOptions).addTo(map);

    overlay.setOpacity(opacity);
    const overlayEditing = overlay.editing as L.DistortableImageEditing & { options?: { opacity?: number } };
    if (overlayEditing.options) {
      overlayEditing.options.opacity = TOOLBAR_TOGGLE_OPACITY;
    }
    overlayRef.current = overlay;
    overlayContainerRef.current = map.getContainer();
    onReady?.(overlay);

    // DistortableImage needs an attached map before editing tools are enabled.
    map.whenReady(() => {
      if (isDisposed || !overlayRef.current || overlayRef.current !== overlay) {
        return;
      }

      if (editable) {
        overlay.editing.enable();
        overlay.select?.();
        overlay.editing.setMode(mode);
        setTimeout(syncAspectRatioToggleButton, 0);
      }
    });

    const handleToolbarSync = () => {
      setTimeout(syncAspectRatioToggleButton, 0);
    };

    overlay.on('toolbar:created', handleToolbarSync);
    overlay.on('select', handleToolbarSync);

    const overlayElement = overlay.getElement();
    const handleOverlayClick = (event: MouseEvent) => {
      const clickHandler = overlayClickRef.current;
      if (!clickHandler) {
        return;
      }

      const latLng = map.mouseEventToLatLng(event);
      clickHandler(latLng.lat, latLng.lng);
    };

    overlayElement?.addEventListener('click', handleOverlayClick, true);

    return () => {
      isDisposed = true;
      overlay.off('toolbar:created', handleToolbarSync);
      overlay.off('select', handleToolbarSync);
      overlayElement?.removeEventListener('click', handleOverlayClick, true);

      if ((overlay as any)._map && overlay.editing?.enabled?.()) {
        overlay.editing.disable();
      }

      onReady?.(null);
      overlayRef.current = null;
      overlayContainerRef.current = null;

      if ((overlay as any)._map && map.hasLayer(overlay)) {
        map.removeLayer(overlay);
      }
    };
  }, [map, imageUrl, editable, placementKey, preserveAspectRatio]);

  useEffect(() => {
    if (!overlayRef.current || !editable) {
      return;
    }

    overlayRef.current.editing.setMode(mode);
  }, [editable, mode]);

  useEffect(() => {
    overlayRef.current?.setOpacity(opacity);
    const overlayEditing = overlayRef.current?.editing as (L.DistortableImageEditing & { options?: { opacity?: number } }) | undefined;
    if (overlayEditing?.options) {
      overlayEditing.options.opacity = TOOLBAR_TOGGLE_OPACITY;
    }
  }, [opacity]);

  useEffect(() => {
    syncAspectRatioToggleButton();
  }, [preserveAspectRatio]);

  return null;
};

export default DistortableImageLayer;
